import { faBed, faBroom, faCalendarCheck, faCalendarDay, faChartPie, faDashboard, faEllipsis, faExpandAlt, faGears, faSackDollar, faSeedling, faTags, faTractor, faUmbrellaBeach, faUser, faUsers, faWallet, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AppLink from "components/common/AppLink";
import { AppRoutes } from "config/router";
import { useEffect, useRef, useState } from "react";
import './AppNav.scss';
import Translate from "components/common/Translate";
import { useLocation, useNavigate } from "react-router-dom";
import { Roles } from "utilities/app-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import Button from "components/common/button/Button";
import { faForkLift } from "utilities/icons/my-fa-icons";

const MENU_HASH = '#menu';

type NavigationList = {
    name: string | React.ReactNode,
    id: string,
    icon: IconDefinition,
    to: string
    items?: NavigationList[],
    hasPermission: boolean,
    expandedIf?: string
}

const AppNav = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const navElement = useRef<HTMLElement>(null);
    const [isLink, setIsLink] = useState(false);
    const [expandedIds, setExpandedIds] = useState<{ id: string, isUserMade: boolean }[]>([]);
    const context = useAppContext();

    const links: NavigationList[] = [
        {
            name: 'Dashboard',
            id: "app-links-dashboard",
            icon: faDashboard,
            to: AppRoutes.DASHBOARD,
            hasPermission: true
        },
        {
            name: <Translate i18nKey="pages.agriculture.main" />,
            id: "app-links-agriculture-main",
            icon: faSeedling,
            to: AppRoutes.AGRICULTURE_OVERVIEW,
            hasPermission: true,
            expandedIf: 'app/agriculture',
            items: [
                {
                    to: AppRoutes.AGRICULTURE_OVERVIEW,
                    name: <Translate i18nKey="pages.overview" />,
                    id: "app-links-agriculture-overview",
                    icon: faChartPie,
                    hasPermission: context.userHasRole(Roles.AGRICULTURE_SALES_READ)
                },
                {
                    to: AppRoutes.AGRICULTURE_OVERVIEW_SALE,
                    name: <Translate i18nKey="pages.agriculture.sale.list" />,
                    id: "app-links-agriculture-sale",
                    icon: faSackDollar,
                    hasPermission: context.userHasRole(Roles.AGRICULTURE_SALES_READ)
                },
                {
                    to: AppRoutes.AGRICULTURE_OVERVIEW_WORK,
                    name: <Translate i18nKey="pages.agriculture.work.harvest.list" />,
                    id: "app-links-agriculture-harvests",
                    icon: faForkLift,
                    hasPermission: context.userHasRole(Roles.AGRICULTURE_WORK_READ)
                },
                {
                    to: AppRoutes.AGRICULTURE_OVERVIEW_MAINTENANCE,
                    name: <Translate i18nKey="pages.agriculture.work.maintenance.list" />,
                    id: "app-links-agriculture-maintenace",
                    icon: faTractor,
                    hasPermission: context.userHasRole(Roles.AGRICULTURE_WORK_READ)
                },
                {
                    to: AppRoutes.AGRICULTURE_OVERVIEW_OTHER,
                    name: <Translate i18nKey="pages.other" />,
                    id: "app-links-agriculture-other",
                    icon: faEllipsis,
                    hasPermission: true
                },
            ]
        },
        {
            name: <Translate i18nKey="pages.accommodation.main" />,
            id: "app-links-accommodation-main",
            icon: faUmbrellaBeach,
            to: AppRoutes.ACCOMMODATION_OVERVIEW_HOME,
            hasPermission: context.userContainsRoles([Roles.ACCOMMODATION_BOOKINGS_READ, Roles.ACCOMMODATION_WORK_READ]),
            expandedIf: 'app/accommodation',
            items: [
                {
                    to: AppRoutes.ACCOMMODATION_OVERVIEW_HOME,
                    name: <Translate i18nKey="pages.accommodation.booking.list" />,
                    id: "app-links-accommodation-booking",
                    icon: faBed,
                    hasPermission: context.userHasRole(Roles.ACCOMMODATION_BOOKINGS_READ)
                },
                {
                    to: AppRoutes.ACCOMMODATION_OVERVIEW_EVENT,
                    name: <Translate i18nKey="pages.accommodation.event.list" />,
                    id: "app-links-accommodation-event",
                    icon: faCalendarDay,
                    hasPermission: context.userHasRole(Roles.ACCOMMODATION_BOOKINGS_READ)
                },
                {
                    to: AppRoutes.ACCOMMODATION_OVERVIEW_AVAILABILITY,
                    name: <Translate i18nKey="pages.accommodation.availability" />,
                    id: "app-links-accommodation-availability",
                    icon: faCalendarCheck,
                    hasPermission: context.userHasRole(Roles.ACCOMMODATION_BOOKINGS_READ)
                },
                {
                    to: AppRoutes.ACCOMMODATION_OVERVIEW_MAINTENANCE,
                    name: <Translate i18nKey="pages.accommodation.work.maintenance.list" />,
                    id: "app-links-accommodation-maintenace",
                    icon: faBroom,
                    hasPermission: context.userHasRole(Roles.ACCOMMODATION_WORK_READ)
                },
                {
                    to: AppRoutes.ACCOMMODATION_OVERVIEW_OVERVIEW,
                    name: <Translate i18nKey="pages.overview" />,
                    id: "app-links-accommodation-overview",
                    icon: faChartPie,
                    hasPermission: context.userHasRole(Roles.ACCOMMODATION_BOOKINGS_READ)
                }
            ]
        },
        {
            name: <Translate i18nKey="pages.people.list" />,
            id: "app-links-people-main",
            icon: faUser,
            to: AppRoutes.PERSON_HOME,
            hasPermission: context.userHasRole(Roles.PERSON_READ),
            expandedIf: 'app/person',
            items: [
                {
                    name: <Translate i18nKey="pages.people.list" />,
                    id: "app-links-people-home",
                    icon: faUsers,
                    to: AppRoutes.PERSON_HOME,
                    hasPermission: context.userHasRole(Roles.PERSON_READ),
                    expandedIf: 'person',
                },
                {
                    name: <Translate i18nKey="pages.people.tag.list" />,
                    id: "app-links-people-tags",
                    icon: faTags,
                    to: AppRoutes.PERSON_TAG_DATALIST,
                    hasPermission: context.userHasRole(Roles.PERSON_READ),
                    expandedIf: 'person/tag',
                },
                {
                    name: <Translate i18nKey="pages.people.wallet.list" />,
                    id: "app-links-people-wallet",
                    icon: faWallet,
                    to: AppRoutes.PERSON_WALLET_DATALIST,
                    hasPermission: context.userHasRole(Roles.ADMIN),
                    expandedIf: 'person/wallet',
                }
            ]
        },
        {
            name: <Translate i18nKey="pages.settings.main" />,
            id: "app-links-settings-mian",
            icon: faGears,
            to: AppRoutes.SETTINGS,
            hasPermission: context.userHasRole(Roles.ADMIN)
        },
    ]

    useEffect(() => {
        setIsLink(location.hash === MENU_HASH ? true : false);
        let ll = [...expandedIds];
        links.filter(x => x.items?.length && x.expandedIf).forEach(x => {
            if (location.pathname.startsWith("/" + x.expandedIf) && !expandedIds.some(e => e.id === x.id)) {
                ll = [...expandedIds, { id: x.id, isUserMade: false }];
            } else if (!location.pathname.startsWith("/" + x.expandedIf) && ll.some(e => e.id === x.id && !e.isUserMade)) {
                ll = ll.filter(e => e.id !== x.id);
            }
        })

        setExpandedIds(ll);
    }, [location])

    const hide = () => {
        if (location.hash === MENU_HASH) {
            navigate(-1);
        } else {
            navElement.current?.classList.add('hidden');
        }
    }

    const toggleExpanded = (id: string) => {
        let ids = [...expandedIds];
        if (ids.some(x => x.id === id)) {
            ids = ids.filter(x => x.id !== id);
        } else {
            ids.push({ id, isUserMade: true });
        }

        setExpandedIds(ids);
    }

    const renderUls = (links: NavigationList[]) => {
        return links.filter(X => X.hasPermission).map(l => {
            const isExpanded = expandedIds.some(x => x.id === l.id);
            let linkClass = l.items?.length
                ? "col-span-4 md:col-span-1 xxl:col-span-4"
                : "col-span-5 md:col-span-2 xxl:col-span-5";

            if (l.expandedIf && location.pathname.startsWith("/" + l.expandedIf)) {
                linkClass += " border-b border-sunrise-300";
            }

            return <ul key={l.id}>
                <li>
                    <div className={`grid grid-cols-5 md:grid-cols-2 xxl:grid-cols-5`}>
                        <AppLink to={l.to} className={linkClass}>
                            <FontAwesomeIcon icon={l.icon} />
                            <span className="text">
                                {l.name}
                            </span>
                        </AppLink>
                        <Button
                            title="expand"
                            theme="link"
                            className={`${l.items?.length ? '' : 'hidden'} md:pl-4 xxl:pl-0 text-gray-500`}
                            onClick={() => toggleExpanded(l.id)}
                        >
                            <FontAwesomeIcon icon={faExpandAlt} />
                        </Button>
                    </div>
                    <div className={`ml-1 border-l-4 border-wi border-gray-700 dark:border-gray-400 ${isExpanded ? '' : 'hidden'}`}>
                        {(l.items || []).map(x => renderUls([x]))}
                    </div>
                </li>
            </ul>;
        });
    }

    return (
        <nav id="app-nav" className={`app-nav ${isLink ? 'link' : ''}`} ref={navElement}>
            <div className="back-ground" onClick={hide}>
            </div>
            <div className="content">
                {renderUls(links)}
            </div>
        </nav>
    );
}

export default AppNav;
